<template>
  <customModal :containermd="true" @closeModal="closeModal">
    <template #title>{{$t("navigations.shell preview")}}</template>

    <template #body>

      <!-- Loading -->
      <div class="text-center py-3" v-if="loading">
        <img src="../../assets/img/loading.gif" width="13" alt="Loading" />
        <div class="text-muted mt-1" style="font-size:12px;">Loading...</div>
      </div>

      <!-- No data -->
      <!-- <div class="text-center py-3 text-muted" style="font-size:12px;" v-else-if="!shell">
        No shell data found.
      </div> -->

      <!-- Paper Preview -->
      <div v-else class="preview-scroll-wrap">

        <!-- Meta info strip -->
        <div class="meta-strip mb-2 d-flex align-items-center justify-content-between">
          <div >
          <span><strong>{{ shell.TemplateName }}</strong></span>
          <span class="ms-3 text-muted">v{{ shell.Version }}</span>
          <span v-if="shell.IsDefault" class="badge-default ms-2">DEFAULT</span>
          <span class="ms-3 text-muted">{{ shell.Font?.family }}, {{ shell.Font?.sizePt }}px</span>
          <span class="ms-3 text-muted">{{ shell.PageLayout?.orientation }}</span>
          </div>
          <div class="">
                <select v-model="selectoption" class="form-select select-wide">
                  <!-- <option value="">Select</option> -->
                  <option value="figure">Figure</option>
                  <option value="table">Table</option>
                  <option value="listing">Listing</option>
                </select>
          </div>
        </div>

        <!-- Paper -->
        <div class="paper-box" :style="{ fontFamily: shell.Font?.family || 'monospace', fontSize: (shell.Font?.sizePt || 10) + 'pt' }">

          <!-- Header rows -->
          <div
            v-for="(row, i) in headerRows"
            :key="'h-' + i"
            class="paper-header-row"
            :class="{ 'border-bottom-line': shell.Header?.borderBottom && i === headerRows.length - 1 }"
          >
            <span class="col-left">{{ getTokenLabel(row.left) }}</span>
            <span class="col-center">{{ getTokenLabel(row.center) }}</span>
            <span class="col-right">{{ getTokenLabel(row.right) }}</span>
          </div>

          <!-- Title -->
          <div
            class="paper-title"
            :style="{
              fontWeight: shell.Title?.bold ? 'bold' : 'normal',
              textAlign: shell.Title?.align || 'left',
            }"
          >
            {{ getTokenLabel(titleText) }}
          </div>

          <!-- Content area -->
          <div class="paper-content" style="padding: 0; border: none;">
            <div v-if="selectoption === 'figure'">
              <img
                src="../../assets/img/shellchart.png"
                alt="Shell Chart"
                style="width: 100%; height: auto; display: block;"
              />
            </div>
            
            <div v-else-if="selectoption === 'table'" class="table-preview-container">
              <table class="clinical-table">
                <thead>
                  <tr>
                    <th v-for="(header, hi) in tableHeaders" :key="'th-' + hi"
                      class="clinical-th"
                      :class="'align-' + header.align">
                      <span class="header-label">{{ header.label }}</span><br v-if="header.sub"/>
                      <span v-if="header.sub" class="header-sub">{{ header.sub }}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <!-- Summary Row -->
                  <tr class="summary-row">
                    <td class="clinical-td text-start">{{ tableSummary.label }}</td>
                    <td v-for="(header, hi) in tableHeaders.slice(1)" :key="'ts-td-' + hi" class="clinical-td text-center">
                      {{ getCellValue(tableSummary, header.label) }}
                    </td>
                  </tr>
                  <!-- Spacer row -->
                  <tr class="spacer-row"><td :colspan="tableHeaders.length"></td></tr>
                  <!-- Sections -->
                  <template v-for="(section, si) in computedTableData" :key="'ts-' + si">
                    <!-- Section Header -->
                    <tr class="section-header-row">
                      <td :colspan="tableHeaders.length">
                        {{ section.section }}
                      </td>
                    </tr>
                    <!-- Term Rows -->
                    <tr v-for="(row, ri) in section.rows" :key="'tr-' + si + '-' + ri" class="term-row">
                      <td class="clinical-td term-name">{{ row.term }}</td>
                      <td v-for="(header, hi) in tableHeaders.slice(1)" :key="'td-' + hi" class="clinical-td text-center">
                        {{ getCellValue(row, header.label) }}
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>

            <div v-else-if="selectoption === 'listing'" class="listing-preview-container">
              <div class="clinical-listing">
                <!-- Header -->
                <div class="clinical-listing-header">
                  <span v-for="(h, hi) in listingHeaders" :key="'lh-'+hi" :class="h.class">
                    {{ h.label }}
                  </span>
                </div>

                <!-- Summary Row -->
                <div class="clinical-listing-summary">
                  <span class="col-term">{{ listSummary.label }}</span>
                  <span v-for="(h, hi) in listingHeaders.slice(1)" :key="'lsum-'+hi" :class="h.class">
                    {{ hi === 0 ? listSummary.group : (hi === 1 ? listSummary.subjects : '—') }}
                  </span>
                </div>

                <!-- Sections -->
                <template v-for="(section, si) in listData" :key="'ls-' + si">
                  <!-- Section Header -->
                  <div class="clinical-listing-section">
                    {{ section.section }}
                  </div>

                  <!-- Term Rows -->
                  <div v-for="(row, ri) in section.rows" :key="'lr-' + si + '-' + ri"
                    class="clinical-listing-term-group">

                    <!-- First group row (shows term name) -->
                    <div class="clinical-listing-row">
                      <span class="col-term term-name">{{ row.term }}</span>
                      <span v-for="(h, hi) in listingHeaders.slice(1)" :key="'lrow-'+hi" :class="h.class">
                        {{ hi === 0 ? row.groups[0].group : (hi === 1 ? row.groups[0].subjects : (hi === 2 ? row.groups[0].events : 'Mild (1)')) }}
                      </span>
                    </div>

                    <!-- Remaining group rows -->
                    <template v-if="!shell?.ColumnFilters || shell?.ColumnFilters.length === 0">
                      <div v-for="(grp, gi) in row.groups.slice(1)" :key="'grp-' + gi"
                        class="clinical-listing-row">
                        <span class="col-term"></span>
                        <span class="col-group">{{ grp.group }}</span>
                        <span class="col-subjects">{{ grp.subjects }}</span>
                        <span class="col-events">{{ grp.events }}</span>
                        <span class="col-severity">{{ grp.severity }}</span>
                      </div>
                    </template>

                    <!-- Total row -->
                    <div class="clinical-listing-total">
                      <span class="col-term"></span>
                      <span class="col-group total-label">Total</span>
                      <span class="col-subjects">{{ row.total.subjects }}</span>
                      <span class="col-events">{{ row.total.events }}</span>
                      <span class="col-severity"></span>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>

          <!-- Footer rows -->
          <div
            v-for="(row, i) in footerRows"
            :key="'f-' + i"
            class="paper-footer-row"
            :class="{ 'border-top-line': shell.Footer?.borderTop && i === 0 }"
          >
            <span class="col-left">{{ getTokenLabel(row.left) }}</span>
            <span class="col-center">{{ getTokenLabel(row.center) }}</span>
            <span class="col-right">{{ getTokenLabel(row.right) }}</span>
          </div>

        </div>
      </div>

    </template>
  </customModal>
</template>

<script src="../shells/ShellPreview.js"></script>

<style scoped>
.meta-strip {
  font-size: 12px;
  padding: 4px 6px;
  background: #f0f4ff;
  border-radius: 3px;
  border: 1px solid #dce6ff;
}
.badge-default {
  background: #28a745;
  color: #fff;
  border-radius: 3px;
  padding: 1px 6px;
  font-size: 10px;
}

/* Paper */
.paper-box {
  border: 1px solid #ddd;
  background: #fff;
  padding: 16px;
  min-height: 500px;
  display: flex;
  flex-direction: column;
}

/* Header row */
.paper-header-row {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  padding-bottom: 4px;
  margin-bottom: 2px;
}
.border-bottom-line {
  border-bottom: 1px solid #333;
  margin-bottom: 6px;
  padding-bottom: 4px;
}

/* Title */
.paper-title {
  margin: 10px 0 8px 0;
  font-size: 11px;
}

/* Content */
.paper-content {
  flex: 1;
  border: 1px solid #e0e0e0;
  padding: 8px;
  color: #aaa;
  font-size: 11px;
  min-height: 300px;
}

/* Footer row */
.paper-footer-row {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  padding-top: 4px;
  margin-top: 2px;
}
.border-top-line {
  border-top: 1px solid #333;
  margin-top: 6px;
  padding-top: 4px;
}

/* Scroll wrapper */
.preview-scroll-wrap {
  max-height: 70vh;
  overflow-y: auto;
  overflow-x: visible;
  padding-right: 4px;
}
.preview-scroll-wrap::-webkit-scrollbar { width: 5px; }
.preview-scroll-wrap::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 3px; }
.preview-scroll-wrap::-webkit-scrollbar-thumb { background: #b0c4de; border-radius: 3px; }
.preview-scroll-wrap::-webkit-scrollbar-thumb:hover { background: #145faf; }

/* Columns */
.col-left   { text-align: left;   flex: 1; }
.col-center { text-align: center; flex: 1; }
.col-right  { text-align: right;  flex: 1; }
.select-wide{
  width: 130px;
}

/* Clinical Table Design */
.table-preview-container {
  background: #fff;
  padding: 8px 12px;
  overflow-x: auto;
  border: 1px solid #eee;
  max-width: 100%;
}

.clinical-table {
  width: 100%;
  border-collapse: collapse;
  font-family: 'Courier New', Courier, monospace;
  color: #000;
}

.clinical-table thead {
  border-top: 1px solid #000;
  border-bottom: 1px solid #000;
}

.clinical-th {
  padding: 6px 8px;
  font-size: 11px;
  font-weight: normal;
  color: #000;
  vertical-align: bottom;
  line-height: 1.3;
}

.clinical-th.align-left {
  text-align: left;
}
.clinical-th.align-center {
  text-align: center;
}
.clinical-th.align-right {
  text-align: right;
}

.header-label {
  font-weight: normal;
}

.header-sub {
  font-weight: normal;
  margin-top: 2px;
}

.clinical-td {
  padding: 4px 8px;
  font-size: 11px;
  font-weight: normal;
  color: #000;
  border: none;
  line-height: 1.3;
}

.clinical-td.text-start {
  text-align: left;
}
.clinical-td.text-center {
  text-align: center;
}
.clinical-td.text-end {
  text-align: right;
}

.summary-row td {
  padding-top: 6px;
  padding-bottom: 6px;
}

.spacer-row td {
  height: 14px;
  border: none;
}

.section-header-row td {
  padding-top: 12px;
  padding-bottom: 4px;
  font-size: 11px;
  font-weight: normal;
  text-transform: uppercase;
  color: #000;
  border: none;
  text-align: left;
}

.term-row td.term-name {
  padding-left: 20px; /* Indent preferred terms */
  text-align: left;
}

.clinical-table tbody {
  border-bottom: 1px solid #000;
}

/* Listing Styles */
.listing-preview-container {
  background: #fff;
  padding: 8px 12px;
  border: 1px solid #eee;
  overflow-x: auto;
  max-width: 100%;
}

.clinical-listing {
  font-family: 'Courier New', Courier, monospace;
  font-size: 11px;
  color: #000;
  border-bottom: 1px solid #000;
}

.clinical-listing-header {
  border-top: 1px solid #000;
  border-bottom: 1px solid #000;
  padding: 6px 8px;
  display: flex;
  gap: 8px;
  font-weight: normal;
  line-height: 1.3;
}

.clinical-listing-header span,
.clinical-listing-summary span,
.clinical-listing-row span,
.clinical-listing-total span {
  display: inline-block;
}

.col-term { width: 220px; text-align: left; }
.col-group { width: 90px; text-align: center; }
.col-subjects { width: 80px; text-align: center; }
.col-events { width: 80px; text-align: center; }
.col-severity { flex: 1; text-align: left; }

.clinical-listing-summary {
  padding: 6px 8px;
  display: flex;
  gap: 8px;
  font-weight: normal;
  line-height: 1.3;
}

.clinical-listing-section {
  padding: 12px 8px 4px 8px;
  font-weight: normal;
  text-transform: uppercase;
  color: #000;
  text-align: left;
}

.clinical-listing-term-group {
  padding: 2px 0;
}

.clinical-listing-row {
  padding: 3px 8px;
  display: flex;
  gap: 8px;
  line-height: 1.3;
}

.clinical-listing-row .term-name {
  padding-left: 20px;
}

.clinical-listing-total {
  padding: 3px 8px;
  display: flex;
  gap: 8px;
  border-top: 1px solid #000;
  border-bottom: 1px solid #000;
  margin-top: 2px;
  line-height: 1.3;
}

.total-label {
  font-weight: bold;
}

.clinical-listing-footer-note {
  margin-top: 15px;
  font-size: 10px;
  color: #555;
  padding: 8px 8px;
  border-top: 1px solid #000;
}
</style>